import React from 'react';

import { fmtDistance } from '../format';
import { FormField, TextInput } from './form';

/**
 * "Jarak dari hub" on the Ubah dialogs (outbound request, shipment). Left
 * empty, Dispatch measures it (Mapbox) when the shipment is planned — the
 * BE's DISTANCE_WMS_CALCULATION_MODE=NONE; ops may type one by hand. Empty + `auto` (the point moved and
 * nothing was typed) = generated again on save.
 */

export interface DistanceDraft {
  /** Metres as typed ('' = not typed). */
  value: string;
  /** Typed by hand in this dialog. */
  touched: boolean;
}

export const distanceDraftOf = (
  metres: number | null | undefined
): DistanceDraft => ({
  value: metres === null || metres === undefined ? '' : String(metres),
  touched: false,
});

/** The metres to send: typed → that; otherwise undefined (server decides). */
export const typedDistance = (d: DistanceDraft): number | undefined =>
  d.touched && d.value.trim() !== '' ? Number(d.value.trim()) : undefined;

/** Local guard: a typed distance is a whole number ≥ 0. */
export const distanceError = (d: DistanceDraft): string | null => {
  if (!d.touched || d.value.trim() === '') return null;
  const n = Number(d.value.trim());
  return Number.isInteger(n) && n >= 0
    ? null
    : 'Jarak (meter) harus bilangan bulat ≥ 0.';
};

interface Props {
  value: DistanceDraft;
  /** The point moved and nothing was typed: regenerated on save. */
  auto: boolean;
  onChange: (next: DistanceDraft) => void;
  error?: string | null;
  testId?: string;
}

const DistanceField: React.FC<Props> = ({
  value,
  auto,
  onChange,
  error,
  testId = 'distance-field',
}) => {
  const typed = value.value.trim();
  const hint =
    auto && !typed
      ? 'Titik berubah — jarak lama dikosongkan, dihitung lagi saat dispatch. Isi manual kalau perlu.'
      : typed && Number.isFinite(Number(typed))
        ? `${fmtDistance(Number(typed))}${value.touched ? ' · diisi manual' : ''}`
        : 'Kosong = dihitung saat dispatch. Isi manual kalau perlu.';
  return (
    <FormField
      label="Jarak dari hub (meter)"
      htmlFor="oo-distance"
      optional
      hint={error ? undefined : hint}
      error={error}
      testId={testId}
    >
      <TextInput
        id="oo-distance"
        size="md"
        icon="ri-route-line"
        inputMode="numeric"
        value={value.value}
        onChange={(v) => onChange({ value: v, touched: true })}
        placeholder={auto ? 'Dihitung saat dispatch' : undefined}
        invalid={!!error}
        className="font-mono"
      />
    </FormField>
  );
};

export default DistanceField;
